import { Component, ChangeDetectionStrategy, inject, input, output, signal } from '@angular/core';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem as PrimeMenuItem } from 'primeng/api';
import { Router } from '@angular/router';
import { User } from '../../../core/models/user.model';
import { AuthService } from '../../../core/services/auth.service';
import { TimeIndicatorComponent } from '../time-indicator/time-indicator.component';
import { NotificationsBellComponent } from '../notifications-bell/notifications-bell.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [MenuModule, TooltipModule, TimeIndicatorComponent, NotificationsBellComponent],
  template: `
    <div class="header">
      <div class="header__left">
        <button type="button" class="header__toggle" (click)="toggleSidebar.emit()" aria-label="Alternar menú lateral">
          <i class="pi pi-bars" aria-hidden="true"></i>
        </button>

        <div class="header__logo-mobile">
          <div class="header__logo-icon">R</div>
          <span class="header__logo-text">RBI Admin</span>
        </div>

        <nav class="header__page" aria-label="Ruta de navegación">
          @if (pageIcon()) {
            <span class="header__page-icon"
              [pTooltip]="pageDescription() || ''" tooltipPosition="bottom"
              [tooltipDisabled]="!pageDescription()" tabindex="0">
              <i [class]="pageIcon()"></i>
            </span>
          }
          @if (pageTitle()) {
            <span class="header__page-title">{{ pageTitle() }}</span>
          }
        </nav>
      </div>

      <button type="button" class="header__mobile-toggle" (click)="toggleTopbar()" aria-label="Alternar menú">
        <i class="pi pi-ellipsis-v" aria-hidden="true"></i>
      </button>

      <div class="header__right" [class.header__right--mobile-active]="mobileTopbarActive()">
        <app-time-indicator />

        <app-notifications-bell />

        <div class="header__profile">
          <button type="button" class="header__profile-btn" (click)="menu.toggle($event)">
            @if (currentUser()?.avatar) {
              <img [src]="currentUser()!.avatar" alt="User avatar" class="header__avatar" />
            } @else {
              <div class="header__avatar-initials">{{ currentUser()?.initials || '?' }}</div>
            }
            <div class="header__profile-info">
              <span class="header__profile-name">{{ currentUser()?.name || 'Invitado' }}</span>
              <span class="header__profile-role">{{ currentUser()?.email || '' }}</span>
            </div>
            <i class="pi pi-chevron-down header__profile-chevron"></i>
          </button>
          <p-menu #menu [model]="userMenuItems" [popup]="true" [appendTo]="'body'" styleClass="header-user-menu"></p-menu>
        </div>
      </div>
    </div>
  `,
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  readonly currentUser = input.required<User | null>();
  readonly pageTitle = input<string>('');
  readonly pageIcon = input<string>('');
  readonly pageDescription = input<string | undefined>(undefined);
  readonly toggleSidebar = output<void>();

  readonly mobileTopbarActive = signal(false);

  toggleTopbar(): void {
    this.mobileTopbarActive.update((value) => !value);
  }

  readonly userMenuItems: PrimeMenuItem[] = [
    { label: 'Perfil', icon: 'pi pi-fw pi-user', command: () => this.handleMenuClick('Profile') },
    // { label: 'Configuracion', icon: 'pi pi-fw pi-cog', command: () => this.handleMenuClick('Settings') },
    { separator: true },
    // { label: 'Desactivar alertas', icon: 'pi pi-fw pi-times', command: () => this.handleMenuClick('Disable') },
    { separator: true },
    { label: 'Cerrar sesion', icon: 'pi pi-fw pi-sign-out', command: () => this.handleMenuClick('SignOut') },
  ];

  private handleMenuClick(action: string): void {
    if (action === 'Profile') {
      void this.router.navigate(['/perfil']);
      return;
    }

    if (action === 'SignOut') {
      this.authService.logout();
      void this.router.navigate(['/login']);
    }
  }
}
